import React from "react";
import { Button } from "@/components/ui/button";
import { AlertTriangle } from "lucide-react";

/**
 * Production-safe error boundary.  A single render failure in one route should
 * never take down the whole shell.  We log the error to the console for
 * post-mortem and surface a recoverable UI to the user.
 */
export default class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    // eslint-disable-next-line no-console
    console.error("[ErrorBoundary]", error, info?.componentStack);
  }

  reset = () => {
    this.setState({ error: null });
  };

  reload = () => {
    window.location.reload();
  };

  render() {
    if (!this.state.error) return this.props.children;

    const msg =
      this.state.error?.message ||
      (typeof this.state.error === "string" ? this.state.error : "Bilinmeyen bir hata oluştu.");

    return (
      <div
        className="min-h-[60vh] flex items-center justify-center px-6"
        data-testid="error-boundary-fallback"
      >
        <div className="max-w-md w-full bg-white border border-amber-200 rounded-xl p-8 text-center shadow-sm">
          <div className="w-12 h-12 rounded-full bg-amber-50 text-amber-700 flex items-center justify-center mx-auto">
            <AlertTriangle className="w-6 h-6" />
          </div>
          <h2 className="mt-4 font-display text-xl font-semibold text-slate-900">
            Bir sorun oluştu
          </h2>
          <p className="mt-2 text-sm text-slate-600 leading-relaxed">
            Sayfa yüklenirken beklenmeyen bir hata oluştu. Verileriniz güvende. Sayfayı
            yenileyebilir veya tekrar deneyebilirsiniz.
          </p>
          <details className="mt-4 text-left text-xs text-slate-500 bg-slate-50 rounded-md p-3">
            <summary className="cursor-pointer font-medium text-slate-600">
              Teknik detaylar
            </summary>
            <pre className="mt-2 whitespace-pre-wrap break-words">{msg}</pre>
          </details>
          <div className="flex items-center justify-center gap-2 mt-6">
            <Button onClick={this.reset} variant="outline" data-testid="error-boundary-retry">
              Tekrar Dene
            </Button>
            <Button onClick={this.reload} className="bg-slate-900 hover:bg-slate-800" data-testid="error-boundary-reload">
              Sayfayı Yenile
            </Button>
          </div>
        </div>
      </div>
    );
  }
}
